import { useState } from 'react';

export interface CompressionOptions {
  quality: number; // 0-100
  format: 'jpeg' | 'webp' | 'png';
  mode: 'quality' | 'filesize';
  targetFileSize?: number; // in KB
  removeExif: boolean;
  lossless?: boolean;
}

interface CompressedImage {
  id: string;
  original: string;
  compressed: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  settings: CompressionOptions;
}

export const useImageCompress = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [compressedImages, setCompressedImages] = useState<CompressedImage[]>([]);

  const getFileSize = (dataUrl: string): number => {
    // Remove data URL prefix
    const base64 = dataUrl.split(',')[1];
    // Calculate size in bytes
    const bytes = (base64.length * 3) / 4;
    return Math.round(bytes / 1024); // Return KB
  };

  const compressImage = async (
    imageUrl: string,
    imageId: string,
    options: CompressionOptions
  ): Promise<{ compressed: string; size: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          canvas.width = img.width;
          canvas.height = img.height;

          ctx.drawImage(img, 0, 0);

          let quality = options.quality / 100;
          const mimeType = options.format === 'jpeg' ? 'image/jpeg' : 
                          options.format === 'webp' ? 'image/webp' : 
                          'image/png';

          // For PNG, we can't control quality, so we use default compression
          if (options.format === 'png') {
            const compressed = canvas.toDataURL(mimeType);
            const size = getFileSize(compressed);
            resolve({ compressed, size });
            return;
          }

          // For target file size mode, iterate to find the right quality
          if (options.mode === 'filesize' && options.targetFileSize) {
            let minQuality = 0.1;
            let maxQuality = 1.0;
            let iterations = 0;
            const maxIterations = 10;

            while (iterations < maxIterations) {
              quality = (minQuality + maxQuality) / 2;
              const compressed = canvas.toDataURL(mimeType, quality);
              const size = getFileSize(compressed);

              if (Math.abs(size - options.targetFileSize) < 10) {
                resolve({ compressed, size });
                return;
              }

              if (size > options.targetFileSize) {
                maxQuality = quality;
              } else {
                minQuality = quality;
              }

              iterations++;
            }
          }

          // Standard quality-based compression
          const compressed = canvas.toDataURL(mimeType, quality);
          const size = getFileSize(compressed);
          resolve({ compressed, size });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const processImages = async (
    images: Array<{ id: string; preview: string }>,
    options: CompressionOptions
  ) => {
    setIsProcessing(true);
    setProgress(0);
    const results: CompressedImage[] = [];

    for (let i = 0; i < images.length; i++) {
      try {
        const originalSize = getFileSize(images[i].preview);
        const { compressed, size } = await compressImage(
          images[i].preview,
          images[i].id,
          options
        );

        results.push({
          id: images[i].id,
          original: images[i].preview,
          compressed,
          originalSize,
          compressedSize: size,
          compressionRatio: ((originalSize - size) / originalSize) * 100,
          settings: options,
        });
      } catch (error) {
        console.error(`Failed to compress image ${images[i].id}:`, error);
      }
      setProgress(((i + 1) / images.length) * 100);
    }

    setCompressedImages(results);
    setIsProcessing(false);
    return results;
  };

  const downloadCompressed = (image: CompressedImage, filename: string) => {
    const link = document.createElement('a');
    link.href = image.compressed;
    const extension = image.settings.format === 'jpeg' ? 'jpg' : image.settings.format;
    link.download = filename.replace(/\.[^/.]+$/, `.${extension}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = (images: CompressedImage[], baseFilename: string) => {
    images.forEach((image, index) => {
      const extension = image.settings.format === 'jpeg' ? 'jpg' : image.settings.format;
      const filename = `${baseFilename}_compressed_${index + 1}.${extension}`;
      downloadCompressed(image, filename);
    });
  };

  return {
    isProcessing,
    progress,
    compressedImages,
    processImages,
    downloadCompressed,
    downloadAll,
  };
};
